// merge two customers. choose the primary (survivor), resolve every field side-by-side, and
// confirm. Strong-identifier conflicts (VAT / tax / country / individual-vs-company) block the
// merge until the user explicitly acknowledges them. orders relink to the primary; the merged
// record is archived (not deleted) and the merge can be undone by an admin.

import { useEffect, useMemo, useState } from 'react';
import { customerDisplayName, type Customer, type CustomerFieldsInput } from '@technohull/shared';
import { useCompareCustomers, useMergeCustomers } from '../features/customers.js';
import { ErrorState, InlineError, LoadingState, Modal, Pill } from './ui.js';
import { IconAlertTriangle } from './icons.js';

const FIELDS: Array<[keyof CustomerFieldsInput, string]> = [
  ['customerType', 'Type'],
  ['companyName', 'Company'],
  ['firstName', 'First name'],
  ['lastName', 'Last name'],
  ['email', 'Email'],
  ['telephone', 'Telephone'],
  ['mobile', 'Mobile'],
  ['profession', 'Profession'],
  ['vatNumber', 'VAT number'],
  ['taxCode', 'Tax code'],
  ['dealer', 'Dealer'],
  ['country', 'Country'],
  ['registeredAddress', 'Registered address'],
  ['street', 'Street'],
  ['postalCode', 'Postal code'],
  ['city', 'City'],
  ['notes', 'Notes'],
];

const val = (c: Customer | null | undefined, k: keyof CustomerFieldsInput): string => (c ? String((c as unknown as Record<string, unknown>)[k] ?? '') : '');

export function MergeModal({ aId, bId, onClose, onDone }: { aId: number; bId: number; onClose: () => void; onDone?: () => void }) {
  const { data, isLoading, error } = useCompareCustomers(aId, bId);
  const merge = useMergeCustomers();

  const [primaryId, setPrimaryId] = useState<number | null>(null);
  const [choice, setChoice] = useState<Record<string, number>>({});
  const [reason, setReason] = useState('');
  const [ack, setAck] = useState(false);

  const a = data?.a ?? null;
  const b = data?.b ?? null;
  const otherId = primaryId == null ? null : primaryId === aId ? bId : aId;
  const byId = (id: number | null | undefined): Customer | null => (id == null ? null : id === a?.id ? a : b);

  // default primary + per-field source once data loads or the primary changes.
  useEffect(() => {
    if (data && primaryId == null) setPrimaryId(data.suggestedPrimaryId);
  }, [data, primaryId]);
  useEffect(() => {
    if (!a || !b || primaryId == null) return;
    const other = primaryId === a.id ? b : a;
    const next: Record<string, number> = {};
    for (const [k] of FIELDS) next[k] = val(byId(primaryId), k) ? primaryId : val(other, k) ? other.id : primaryId;
    setChoice(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryId, a, b]);

  const resolved = useMemo<CustomerFieldsInput | null>(() => {
    if (!a || !b || primaryId == null) return null;
    const r: Record<string, string> = {};
    for (const [k] of FIELDS) r[k] = val(byId(choice[k] ?? primaryId), k);
    if (r.customerType !== 'company' && r.customerType !== 'individual') r.customerType = val(byId(primaryId), 'customerType') || 'individual';
    return r as unknown as CustomerFieldsInput;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choice, a, b, primaryId]);

  // the record a value belongs to, as the column heads read (★ = the chosen primary)
  const src = (id: number) => `#${id}${primaryId === id ? ' ★' : ''}`;
  const strongConflict = !!data?.result.strongConflict;
  const canMerge = !!primaryId && !!resolved && (!strongConflict || ack) && !merge.isPending;

  const doMerge = () => {
    if (!primaryId || !otherId || !resolved) return;
    merge.mutate(
      { primaryId, mergedIds: [otherId], resolved, reason, acknowledgeConflict: ack },
      { onSuccess: () => { onDone?.(); onClose(); } },
    );
  };

  return (
    <Modal
      title="Merge customers"
      width={780}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={!canMerge}
            title={strongConflict && !ack ? 'Tick the identifier-conflict acknowledgement above to merge' : undefined}
            onClick={doMerge}
          >
            {/* name the survivor on the final, destructive step */}
            {merge.isPending ? 'Merging…' : primaryId ? `Merge into #${primaryId}` : 'Merge'}
          </button>
        </>
      }
    >
      {isLoading && <LoadingState />}
      {error && <ErrorState error={error} />}
      {a && b && (
        <>
          <div style={{ marginBottom: 10 }}>
            <div className="field-label">Primary record (survivor) - orders move here, the other is archived</div>
            <div className="merge-primary">
              {[a, b].map((c) => (
                <label key={c.id} className={primaryId === c.id ? 'on' : undefined}>
                  <input type="radio" name="primary" checked={primaryId === c.id} onChange={() => setPrimaryId(c.id)} />
                  <span>
                    <div style={{ fontWeight: 600 }}>{customerDisplayName(c)} <Pill kind="info">#{c.id}</Pill></div>
                    <div className="muted" style={{ fontSize: 11 }}>{c.id === aId ? data?.aOrders : data?.bOrders} orders{data?.suggestedPrimaryId === c.id ? ' · suggested' : ''}</div>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {strongConflict && (
            <div style={{ background: 'var(--err-bg)', border: '1px solid var(--err-bd)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 10 }}>
              <div style={{ fontWeight: 600, color: 'var(--err-fg)' }}><IconAlertTriangle size={16} /> Strong identifier conflict</div>
              <ul style={{ margin: '4px 0 8px', paddingLeft: 18, fontSize: 12 }}>
                {data?.result.conflicting.filter((c) => c.strong).map((c, i) => (
                  <li key={i}>{c.field}: “{c.a || '-'}” ≠ “{c.b || '-'}”</li>
                ))}
              </ul>
              <label className="check-row" style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} />
                These are the same customer - merge despite the conflict.
              </label>
            </div>
          )}

          <div className="field-label">Resolve fields (pick which value the primary keeps)</div>
          {/* phones stack each field: its name, then one full-width choice row per record (data-src) */}
          <table className="tbl merge-resolve" style={{ marginTop: 4 }}>
            <thead><tr><th>Field</th><th>{src(aId)}</th><th>{src(bId)}</th></tr></thead>
            <tbody>
              {FIELDS.map(([k, label]) => {
                const av = val(a, k);
                const bv = val(b, k);
                if (!av && !bv) return null;
                const differ = av && bv && av.toLowerCase() !== bv.toLowerCase();
                const sel = choice[k] ?? primaryId ?? aId;
                const cell = (cid: number, v: string) => (
                  <td
                    data-src={src(cid)}
                    className={differ ? 'pick' : undefined}
                    style={{ cursor: differ ? 'pointer' : 'default', background: sel === cid && differ ? 'var(--accent-weak)' : undefined }}
                    onClick={() => differ && setChoice((p) => ({ ...p, [k]: cid }))}
                  >
                    {differ && <input type="radio" name={`f_${k}`} aria-label={`${label}: ${v || 'empty'} (#${cid})`} checked={sel === cid} onChange={() => setChoice((p) => ({ ...p, [k]: cid }))} style={{ marginRight: 6 }} />}
                    {v || <span className="muted">-</span>}
                  </td>
                );
                return (
                  <tr key={k}>
                    <td className="muted">{label}{differ && <>{' '}<Pill kind="warn">differs</Pill></>}</td>
                    {cell(aId, av)}
                    {cell(bId, bv)}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <label style={{ display: 'block', marginTop: 10 }}>
            <span className="field-label">Reason (optional)</span>
            <input className="field" style={{ width: '100%' }} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. same customer, duplicate data entry" />
          </label>
        </>
      )}
      {/* a merge failure shows here, by the Merge button — not at the top of the scrolled body */}
      {merge.isError && <InlineError error={merge.error} onDismiss={() => merge.reset()} />}
    </Modal>
  );
}
